import { FC, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { EnterpriseAuthImagePanel } from "./component/ImagePanel";
import { EnterpriseAuthContainer } from "./component/EnterpriseAuthContainer";
import { AuthHeader } from "./component/AuthHeader";
import { AuthAlternative } from "./component/AuthAlternative";
import InputField from "./component/EnterpriseInputField";
import PasswordInput from "./component/EnterprisePasswordInput";
import Button from "./component/EnterpriseButton";
import { emptySigninFormValue, signinSchema } from "./validation";
import { SigninApi } from "../../API/auth";
import type { SigninResult } from "../../API/auth";
import { handleSigninVerification } from "./HandleSigninNavigation";

export const SigninPage: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const {
    errors,
    handleChange,
    handleBlur,
    values,
    touched,
    handleSubmit,
    isValid,
    dirty,
  } = useFormik({
    initialValues: emptySigninFormValue,
    validationSchema: signinSchema,
    onSubmit: async (formValues) => {
      setIsLoading(true);

      try {
        const response = await SigninApi<SigninResult>(formValues);
        const result = response.data;

        const search = new URLSearchParams(location.search);
        const redirect = search.get("redirect");

        if (result.requires2FA === true) {
          if (!result.challengeId) {
            throw new Error("The verification challenge is missing.");
          }

          navigate("/enterprise/signin-verification", {
            state: {
              challengeId: result.challengeId,
              redirect,
            },
          });

          return;
        }

        handleSigninVerification(result, redirect);
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "Unable to sign in. Please try again.",
        );
      } finally {
        setIsLoading(false);
      }
    },
  });

  const isButtonDisabled = !isValid || !dirty || isLoading;

  return (
    <div className="flex h-screen flex-col md:flex-row">
      <EnterpriseAuthContainer>
        <>
          <AuthHeader
            title="Sign In"
            subtitle="Create an account to get started"
          />

          <form onSubmit={handleSubmit}>
            <InputField
              name="email"
              placeholder="e.g johndoe@example.com"
              id="Login_username"
              label="Email"
              error={(touched.email && errors.email) || ""}
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
            />

            <PasswordInput
              name="password"
              label="Password"
              id="Login_password"
              placeholder="••••••••••••"
              error={(touched.password && errors.password) || ""}
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
            />

            <Link
              to="/enterprise/forget-password"
              className="flex justify-end text-sm font-medium font-satoshi text-bblack-1 hover:cursor-pointer"
            >
              Forget Password
            </Link>

            <Button
              text="Sign In"
              className="w-full mt-6"
              type={isButtonDisabled ? "muted" : "primary"}
              disabled={isButtonDisabled}
              isLoading={isLoading}
            />
          </form>

          <AuthAlternative
            description="Don't have an account?"
            buttonText="Sign Up"
            buttonLink="/enterprise/signup"
            googleButtonText="Sign in with Google"
          />
        </>
      </EnterpriseAuthContainer>

      <EnterpriseAuthImagePanel />
    </div>
  );
};
